import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ExternalLink, Radio, Briefcase, MessageSquare, MapPin, Users, Mic, CalendarDays } from "lucide-react";
import { PublicHeader, Footer } from "../components/PublicChrome";
import { FeedbackDialog } from "../components/public/FeedbackDialog";
import { SentimentBadge, SourceTag, SampleBadge } from "../components/Badges";
import { useApi, ago } from "../lib/api";
import { RadioPlayer, TvPlayer } from "../components/public/LivePlayer";
import { PEOPLE } from "../lib/heritage";
import { Linkify } from "../components/heritage/Linkify";

const PERSON_SLUG = { "Dr. Osei Kwame Despite": "osei-kwame-despite", "Dr. Ernest Ofori Sarpong": "ernest-ofori-sarpong", "Kennedy Osei": "kennedy-osei" };

const Block = ({ icon: I, title, children, testId }) => (
  <div className="panel p-6" data-testid={testId}>
    <div className="flex items-center gap-2 mb-4"><I size={16} className="text-brand-green" /><span className="eyebrow">{title}</span></div>
    {children}
  </div>
);

const Timetable = ({ live }) => (
  <Block icon={CalendarDays} title={`${live.type === "tv" ? "Programme guide" : "Timetable"} · Ghana time (GMT)`} testId="entity-timetable">
    <div className="divide-y divide-[color:var(--line)]">
      {live.timetable.map((s, i) => {
        const on = live.now && live.now.title === s.title && live.now.start === s.start && live.now.days === s.days;
        return (
          <div key={i} className={`grid grid-cols-[92px_1fr] sm:grid-cols-[110px_1fr_auto] gap-3 py-3 items-start ${on ? "bg-[#F4F8F5] -mx-2 px-2 rounded" : ""}`} data-testid={`timetable-row-${i}`}>
            <span className="font-data text-xs text-brand-green">{s.start ? `${s.start}–${s.end}` : "Time TBC"}</span>
            <div><div className="text-sm text-ink font-semibold">{s.title} {on && <span className="pill pill-gold ml-1">On air</span>}</div><div className="text-xs text-ink-3">{s.host}</div></div>
            <span className="col-start-2 sm:col-start-auto font-data text-[10px] text-ink-3 uppercase">{s.days}</span>
          </div>
        );
      })}
    </div>
    <p className="text-[11px] text-ink-3 mt-3">Source: {live.source}. Gaps between shows are music & continuity.</p>
  </Block>
);

export default function Entity() {
  const { slug } = useParams();
  const { data: b, error } = useApi(`/public/brands/${slug}`);
  const [fb, setFb] = useState(false);
  if (error) return <div data-testid="entity-not-found"><PublicHeader /><p className="p-10 text-ink-3">{error}</p></div>;
  if (!b) return <div><PublicHeader /><p className="p-10 font-data text-xs text-ink-3">Loading establishment…</p></div>;
  return (
    <div data-testid={`entity-page-${slug}`}>
      <PublicHeader onFeedback={() => setFb(true)} />
      <section className="bg-brand-deep text-white relative overflow-hidden">
        {b.banner && <img src={b.banner} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" data-testid="entity-banner" />}
        {b.banner && <div className="absolute inset-0 bg-gradient-to-r from-[#064D29] via-[#064D29]/85 to-[#064D29]/40" />}
        {b.banner_is_illustration && <span className="absolute bottom-2 right-3 font-data text-[9px] uppercase tracking-wider text-white/50">Illustration</span>}
        <div className="relative max-w-6xl mx-auto px-5 md:px-8 py-14">
          <Link to="/organisation" className="font-data text-[10px] uppercase tracking-wider text-white/60 hover:text-white" data-testid="entity-back-org">← The Organisation</Link>
          <div className="eyebrow !text-[color:var(--gold-hi)] mt-6 mb-3">{b.kind}</div>
          <div className="flex items-center gap-4 sm:gap-5 min-w-0">
            {b.logo && <img src={b.logo} alt={`${b.name} logo`} className="w-16 h-16 sm:w-20 sm:h-20 rounded-full object-contain bg-white p-1.5 ring-2 ring-[color:var(--gold-hi)] shrink-0" data-testid="entity-logo" />}
            <h1 className="font-display text-3xl sm:text-5xl font-extrabold min-w-0 break-words [overflow-wrap:anywhere]">{b.name}</h1>
          </div>
          <p className="text-white/85 mt-4 max-w-3xl text-base md:text-lg leading-relaxed"><Linkify text={b.about} light /></p>
          <div className="flex flex-wrap gap-2 mt-6">
            {b.links.map(([l, u]) => (
              <a key={u} href={u} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 bg-white text-[#064D29] font-semibold text-sm px-4 py-2 rounded border-b-2 border-[color:var(--gold-hi)] hover:bg-[color:var(--gold-hi)] transition-colors" data-testid={`entity-link-${l.toLowerCase().replace(/\W+/g, "-")}`}>
                {l} <ExternalLink size={13} />
              </a>
            ))}
            {b.links.length === 0 && <span className="text-xs text-white/60">Official website: not found publicly</span>}
          </div>
        </div>
      </section>
      {b.live && (
        <section className="max-w-6xl mx-auto px-5 md:px-8 -mt-6 relative z-10" data-testid="entity-live-player">
          {b.live.tv_embed ? <TvPlayer station={b.live} /> : <RadioPlayer stationKey={b.live.key} />}
        </section>
      )}
      <section className="max-w-6xl mx-auto px-5 md:px-8 py-12 grid lg:grid-cols-3 gap-6">
        <div className="space-y-6">
          <Block icon={Users} title="Ownership & principals" testId="entity-ownership">
            {b.entity ? (
              <>
                <div className="text-sm text-ink font-semibold">{b.entity.name}</div>
                <div className="text-xs text-ink-3">{b.entity.sector}</div>
                <span className="pill pill-gold mt-2">{b.entity.ownership}</span>
                <div className="mt-4 space-y-1">
                  {b.entity.principals.map((p) => <Link key={p} to={`/people/${PERSON_SLUG[p]}`} className="block text-sm text-brand-green hover:underline" data-testid={`entity-principal-${PERSON_SLUG[p]}`}>{PEOPLE[PERSON_SLUG[p]]?.name || p} →</Link>)}
                </div>
              </>
            ) : <Link to="/people/osei-kwame-despite" className="text-sm text-brand-green hover:underline">Dr. Osei Kwame Despite →</Link>}
            <div className="mt-3"><SourceTag source="DGC entity map" /></div>
          </Block>
          {b.live && (
            <Block icon={Radio} title="On air now" testId="entity-live">
              <div className="text-ink font-semibold" data-testid="entity-now-title">{b.live.now?.title || "Music & continuity"}</div>
              {b.live.now && <div className="text-xs text-ink-3">{b.live.now.start}–{b.live.now.end} · {b.live.now.host}</div>}
            </Block>
          )}
          {b.live?.team?.length > 0 && (
            <Block icon={Mic} title="Presenters & DJs" testId="entity-team">
              <div className="space-y-2">
                {b.live.team.map(([n, r]) => <div key={n + r} className="text-sm"><div className="text-ink font-semibold">{n}</div><div className="text-[11px] text-ink-3">{r}</div></div>)}
              </div>
            </Block>
          )}
          {b.museum && (
            <Block icon={MapPin} title="Visit" testId="entity-visit">
              <a href="/hub#museum" className="btn-gold w-full" data-testid="entity-book-visit">Book a visit</a>
            </Block>
          )}
          <Block icon={MessageSquare} title="Talk to us" testId="entity-feedback">
            <button onClick={() => setFb(true)} className="btn-ghost w-full" data-testid="entity-feedback-btn">Send feedback</button>
          </Block>
        </div>
        <div className="lg:col-span-2 space-y-6">
          {b.live?.timetable && <Timetable live={b.live} />}
          {b.product_list.length > 0 && (
            <Block icon={MapPin} title="Products & where to buy" testId="entity-products">
              <div className="grid sm:grid-cols-2 gap-3">
                {b.product_list.map((p) => (
                  <div key={p.id} className="flex gap-3 items-center bg-[#F4F8F5] rounded p-2">
                    <img src={p.image} alt={p.name} className="w-16 h-16 object-contain bg-white rounded border border-[color:var(--line)]" />
                    <div><div className="text-sm text-ink font-semibold">{p.name}</div><div className="text-[11px] text-ink-3">{p.where_to_buy}</div><a href={`/shop#${p.id}`} className="text-xs text-brand-green font-semibold hover:underline" data-testid={`entity-order-${p.id}`}>Order in DGC Marketplace →</a></div>
                  </div>
                ))}
              </div>
            </Block>
          )}
          <Block icon={Radio} title={`In the news · ${b.news.length} mentions`} testId="entity-news">
            <div className="divide-y divide-[color:var(--line)]">
              {b.news.map((a) => (
                <a key={a.article_id} href={a.url} target="_blank" rel="noreferrer" className="flex items-start justify-between gap-3 py-3 group">
                  <span className="text-sm text-ink group-hover:text-brand-green">{a.title}<span className="block font-data text-[10px] text-ink-3 mt-1">Peacefmonline.com · {ago(a.published_at)}</span></span>
                  <SentimentBadge value={a.sentiment} />
                </a>
              ))}
              {b.news.length === 0 && <p className="text-sm text-ink-3">No mentions in the scraped Peacefmonline.com feed yet. The scraper refreshes hourly.</p>}
            </div>
          </Block>
          <Block icon={Briefcase} title={`Careers · ${b.jobs.length} open roles`} testId="entity-jobs">
            {b.jobs.map((j) => <div key={j.id} className="flex justify-between items-center py-2 border-b border-[color:var(--line)]/60 text-sm"><span className="text-ink">{j.title} {j.is_sample && <SampleBadge label="Sample" />}</span><span className="text-ink-3 text-xs">{j.location}</span></div>)}
            {b.jobs.length === 0 && <p className="text-sm text-ink-3">No open roles right now — join the talent community.</p>}
            <Link to="/careers" className="btn-ghost mt-4" data-testid="entity-careers-link">Go to Careers</Link>
            {b.staff_count > 0 && <p className="text-[11px] text-ink-3 mt-3">{b.staff_count} staff tracked in the Operations Hub (internal).</p>}
          </Block>
        </div>
      </section>
      <Footer />
      <FeedbackDialog open={fb} onOpenChange={setFb} />
    </div>
  );
}
